/**
 * PM Workload — active projects + jobs per Project Manager.
 *
 * Tom Clancy, 2026-06-18:
 *   "I need to be able to see the number of projects (and jobs — LTG/Shade/
 *    AV/IT, Fixtures) each PM is currently working on. Numbers, dollar value,
 *    and each project's status."
 *
 * Data source: /api/pm-workload — Sage JCM_MASTER__JOB filtered to Status !=
 * 'Closed', grouped by Project_Manager, projects assembled with the same
 * site-key logic the New Sales report uses.
 *
 * Standing rules applied:
 *   • Every column on every table is sortable (Tom 2026-04-30).
 *   • Items under headings are alphabetized unless explicitly otherwise.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, CircularProgress, Alert, Chip, Button, TextField,
  Stack, IconButton, Tooltip, Collapse,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowRight as ArrowRightIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import api from '../services/api';

// ── Formatting helpers ────────────────────────────────────────────────────
const fmtMoney = (v) => {
  if (v == null || isNaN(parseFloat(v))) return '—';
  return parseFloat(v).toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  });
};
const fmtMoneyCompact = (v) => {
  if (v == null || isNaN(parseFloat(v))) return '—';
  const n = parseFloat(v);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};
const fmtInt = (v) => (v == null ? '—' : Number(v).toLocaleString('en-US'));

// ── Status chip — colour-coded per Tom's standing visual language ─────────
function StatusChip({ status }) {
  const s = String(status || '').toLowerCase();
  if (s.includes('hold')) {
    return <Chip label="Hold" size="small" color="warning" sx={{ fontWeight: 600 }} />;
  }
  if (s === 'closed') {
    return <Chip label="Closed" size="small" color="success" sx={{ fontWeight: 600 }} />;
  }
  if (s === '' || s === 'active') {
    return <Chip label="Active" size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />;
  }
  return <Chip label={status} size="small" variant="outlined" sx={{ fontWeight: 600 }} />;
}

// ── KPI card ──────────────────────────────────────────────────────────────
function KpiCard({ title, value, subtitle }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ── Sortable comparator (stable) ──────────────────────────────────────────
function descendingComparator(a, b, orderBy) {
  const av = a?.[orderBy], bv = b?.[orderBy];
  // numeric vs string
  const aNum = typeof av === 'number' ? av : parseFloat(av);
  const bNum = typeof bv === 'number' ? bv : parseFloat(bv);
  const bothNumeric = !isNaN(aNum) && !isNaN(bNum) && (typeof av !== 'string' || /^[\d.,-]+$/.test(av));
  if (bothNumeric) return bNum - aNum;
  return String(bv ?? '').localeCompare(String(av ?? ''));
}
function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(arr, comparator) {
  const indexed = arr.map((el, idx) => [el, idx]);
  indexed.sort((a, b) => {
    const c = comparator(a[0], b[0]);
    if (c !== 0) return c;
    return a[1] - b[1];
  });
  return indexed.map(p => p[0]);
}

// ── Project sub-row ───────────────────────────────────────────────────────
function ProjectRow({ project }) {
  const [open, setOpen] = useState(false);
  const delta = project.revised_contract - project.original_contract;
  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ width: 36 }}>
          <IconButton size="small" onClick={() => setOpen(o => !o)}>
            {open ? <ArrowDownIcon /> : <ArrowRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>{project.project_name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {project.ar_customer || '—'}
            {project.customer_name && project.customer_name !== project.project_name ? ` · ${project.customer_name}` : ''}
            {project.city ? ` · ${project.city}${project.state ? `, ${project.state}` : ''}` : ''}
          </Typography>
        </TableCell>
        <TableCell><StatusChip status={project.status} /></TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {project.disciplines.map(d => (
              <Chip key={d} label={d} size="small" variant="outlined" sx={{ fontSize: 11 }} />
            ))}
          </Stack>
        </TableCell>
        <TableCell align="right">{fmtInt(project.job_count)}</TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={600}>{fmtMoney(project.revised_contract)}</Typography>
          {Math.abs(delta) > 0.5 && (
            <Typography variant="caption" color={delta >= 0 ? 'success.main' : 'error.main'}>
              {delta >= 0 ? '+' : ''}{fmtMoneyCompact(delta)} vs sold
            </Typography>
          )}
        </TableCell>
        <TableCell>{project.salesman}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Job #</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Discipline</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Sold $</TableCell>
                    <TableCell align="right">Current $</TableCell>
                    <TableCell>Est. start</TableCell>
                    <TableCell>Est. comp.</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {project.jobs.map(j => (
                    <TableRow key={j.job_number}>
                      <TableCell><code>{j.job_number}</code></TableCell>
                      <TableCell>{j.description || '—'}</TableCell>
                      <TableCell>{j.discipline}</TableCell>
                      <TableCell><StatusChip status={j.status} /></TableCell>
                      <TableCell align="right">{fmtMoney(j.original_contract)}</TableCell>
                      <TableCell align="right">{fmtMoney(j.revised_contract)}</TableCell>
                      <TableCell>{j.est_start ? new Date(j.est_start).toLocaleDateString() : '—'}</TableCell>
                      <TableCell>{j.est_comp  ? new Date(j.est_comp).toLocaleDateString()  : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ── PM row (summary + expandable projects) ─────────────────────────────────
function PmRow({ pm }) {
  const [open, setOpen] = useState(false);
  const [projOrderBy, setProjOrderBy] = useState('revised_contract');
  const [projOrder, setProjOrder] = useState('desc');
  const handleProjSort = (col) => {
    if (projOrderBy === col) setProjOrder(o => (o === 'desc' ? 'asc' : 'desc'));
    else { setProjOrderBy(col); setProjOrder('desc'); }
  };
  const sortedProjects = useMemo(
    () => stableSort(pm.projects, getComparator(projOrder, projOrderBy)),
    [pm.projects, projOrder, projOrderBy]
  );

  const d = pm.by_discipline;
  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 36 }}>
          <IconButton size="small" onClick={() => setOpen(o => !o)}>
            {open ? <ArrowDownIcon /> : <ArrowRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell><Typography variant="body2" fontWeight={600}>{pm.pm}</Typography></TableCell>
        <TableCell align="right">{fmtInt(pm.project_count)}</TableCell>
        <TableCell align="right">{fmtInt(pm.job_count)}</TableCell>
        <TableCell align="right">{d.av  || '—'}</TableCell>
        <TableCell align="right">{d.it  || '—'}</TableCell>
        <TableCell align="right">{d.ltg || '—'}</TableCell>
        <TableCell align="right">{d.ltfx|| '—'}</TableCell>
        <TableCell align="right">{d.shd || '—'}</TableCell>
        <TableCell align="right">{d.ht  || '—'}</TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={600}>{fmtMoney(pm.revised_contract)}</Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={11} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.default' }}>
              <Typography variant="overline" color="text.secondary">
                Projects ({pm.projects.length}) — click a row to see its jobs
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell />
                      {[
                        { id: 'project_name', label: 'Project' },
                        { id: 'status',      label: 'Status' },
                      ].map(h => (
                        <TableCell key={h.id}>
                          <TableSortLabel
                            active={projOrderBy === h.id}
                            direction={projOrderBy === h.id ? projOrder : 'asc'}
                            onClick={() => handleProjSort(h.id)}
                          >
                            {h.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                      <TableCell>Disciplines</TableCell>
                      {[
                        { id: 'job_count',        label: 'Jobs',     align: 'right' },
                        { id: 'revised_contract', label: 'Current $', align: 'right' },
                        { id: 'salesman',         label: 'Salesman' },
                      ].map(h => (
                        <TableCell key={h.id} align={h.align || 'left'}>
                          <TableSortLabel
                            active={projOrderBy === h.id}
                            direction={projOrderBy === h.id ? projOrder : 'asc'}
                            onClick={() => handleProjSort(h.id)}
                          >
                            {h.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedProjects.map(p => (
                      <ProjectRow key={p.project_key} project={p} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────
export default function PMWorkload() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('revised_contract');
  const [order, setOrder] = useState('desc');

  const load = useCallback(async (force = false) => {
    try {
      if (force) setRefreshing(true); else setLoading(true);
      const url = force ? '/pm-workload/refresh' : '/pm-workload';
      const res = force ? await api.post(url) : await api.get(url);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load PM workload');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const handleSort = (col) => {
    if (orderBy === col) setOrder(o => (o === 'desc' ? 'asc' : 'desc'));
    else { setOrderBy(col); setOrder('desc'); }
  };

  const filteredPms = useMemo(() => {
    if (!data?.pms) return [];
    const q = search.trim().toLowerCase();
    let rows = q
      ? data.pms.filter(p =>
          p.pm.toLowerCase().includes(q) ||
          p.projects.some(pr =>
            (pr.project_name || '').toLowerCase().includes(q) ||
            (pr.customer_name || '').toLowerCase().includes(q) ||
            (pr.ar_customer  || '').toLowerCase().includes(q) ||
            pr.jobs.some(j => String(j.job_number).toLowerCase().includes(q))
          )
        )
      : data.pms;
    // Map disciplines into flat sortable fields.
    rows = rows.map(p => ({
      ...p,
      d_av: p.by_discipline.av,
      d_it: p.by_discipline.it,
      d_ltg: p.by_discipline.ltg,
      d_ltfx: p.by_discipline.ltfx,
      d_shd: p.by_discipline.shd,
      d_ht: p.by_discipline.ht,
    }));
    return stableSort(rows, getComparator(order, orderBy));
  }, [data, search, order, orderBy]);

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const totals = data?.totals || {};

  const headers = [
    { id: 'pm',                label: 'Project Manager', align: 'left'  },
    { id: 'project_count',     label: 'Projects',        align: 'right' },
    { id: 'job_count',         label: 'Jobs',            align: 'right' },
    { id: 'd_av',              label: 'AV',              align: 'right' },
    { id: 'd_it',              label: 'IT',              align: 'right' },
    { id: 'd_ltg',             label: 'Lighting',        align: 'right' },
    { id: 'd_ltfx',            label: 'Fixtures',        align: 'right' },
    { id: 'd_shd',             label: 'Shades',          align: 'right' },
    { id: 'd_ht',              label: 'HT',              align: 'right' },
    { id: 'revised_contract',  label: 'Active $',        align: 'right' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>PM Workload</Typography>
          <Typography variant="body2" color="text.secondary">
            Active projects and jobs per Project Manager. Source: Sage JCM_MASTER__JOB
            where Status ≠ Closed. One project = multiple Sage jobs (AV/IT, Lighting,
            Shades, Fixtures, HT) grouped by D-Tools site name.
          </Typography>
          {data?.generated_at && (
            <Typography variant="caption" color="text.secondary">
              Last refreshed {new Date(data.generated_at).toLocaleString()}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small" placeholder="Search PM, project, AR, job…"
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} /> }}
            sx={{ width: 280 }}
          />
          <Tooltip title="Refresh from Sage">
            <span>
              <Button
                variant="outlined" startIcon={<RefreshIcon />}
                onClick={() => load(true)} disabled={refreshing}
              >
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* KPI cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <KpiCard title="PMs with active work" value={fmtInt(totals.pm_count)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard title="Active projects" value={fmtInt(totals.project_count)} subtitle="site-grouped" />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard title="Active jobs" value={fmtInt(totals.job_count)} subtitle="Sage jobs (all disciplines)" />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard
            title="Active $ on the books"
            value={fmtMoney(totals.revised_contract)}
            subtitle="revised contract today"
          />
        </Grid>
      </Grid>

      {/* Discipline mix strip */}
      {totals.by_discipline && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="overline" color="text.secondary">Active jobs by discipline</Typography>
          <Stack direction="row" spacing={3} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            <Stat label="AV/Video"          value={totals.by_discipline.av}   />
            <Stat label="IT/Networking"     value={totals.by_discipline.it}   />
            <Stat label="Lighting Control"  value={totals.by_discipline.ltg}  />
            <Stat label="Lighting Fixtures" value={totals.by_discipline.ltfx} />
            <Stat label="Shades"            value={totals.by_discipline.shd}  />
            <Stat label="Home Theater"      value={totals.by_discipline.ht}   />
            {totals.by_discipline.other > 0 && <Stat label="Other" value={totals.by_discipline.other} />}
          </Stack>
        </Paper>
      )}

      {/* Main table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ width: 36 }} />
              {headers.map(h => (
                <TableCell key={h.id} align={h.align}>
                  <TableSortLabel
                    active={orderBy === h.id}
                    direction={orderBy === h.id ? order : 'asc'}
                    onClick={() => handleSort(h.id)}
                  >
                    <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                      {h.label}
                    </Typography>
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPms.map(pm => <PmRow key={pm.pm} pm={pm} />)}
            {filteredPms.length === 0 && (
              <TableRow>
                <TableCell colSpan={headers.length + 1} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {search ? 'No matches.' : 'No active PM workload found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function Stat({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>{fmtInt(value)}</Typography>
    </Box>
  );
}
